import "../styles/global.css";

import { MetalProvider } from "@brexhq/metal-theming";
import { BodyScrollProvider } from "@brexhq/metal-utils";
import { SpeedInsights } from "@vercel/speed-insights/next";
import { useSetAtom } from "jotai";
import Cookies from "js-cookie";
import { NextPage } from "next";
import type { AppProps } from "next/app";
import NextLink from "next/link";
import { useRouter } from "next/router";
import Script from "next/script";
import { DefaultSeo, OrganizationJsonLd } from "next-seo";
import React, { ReactElement, ReactNode, useEffect, useRef } from "react";

import { GradientDefinitionSVG } from "@brexhq/next/src/components/building-blocks/IconPicker/IconPicker";
import {
  AnalyticsContextProvider,
  initialState,
} from "@brexhq/next/src/contexts/Analytics";
import { SiteProvider } from "@brexhq/next/src/contexts/Site";
import { ThemeProvider } from "@brexhq/next/src/contexts/Theme";
import useLocation from "@brexhq/next/src/hooks/useLocation";
import {
  createApplicationLaunchedEvent,
  createApplicationRenderedEvent,
} from "@brexhq/next/src/telemetry/analytics";
import { PageViewedEvent } from "@brexhq/next/src/telemetry/analyticsEvents";
import { marketingAttributionProperties } from "@brexhq/next/src/utils/analytics";
import { app, CompositionRoot } from "@brexhq/next/src/utils/compositionRoot";
import deleteCookie from "@brexhq/next/src/utils/deleteCookie";
import { getServerSideCurrentUrl } from "@brexhq/next/src/utils/getServerSideCurrentUrl";
import { parseQueryString } from "@brexhq/next/src/utils/parseQueryString";
import {
  logScriptLoadingError,
  verifyScriptsLoaded,
} from "@brexhq/next/src/utils/scripts";
import standardizePath from "@brexhq/next/src/utils/standardizePath";
import updateCookies from "@brexhq/next/src/utils/updateCookies";
import { ExternalScripts } from "@brexhq/next/types/externalScripts";
import { LazyMotionProvider } from "@brexhq/shared/utils/motion/LazyMotion";

import Layout from "../layouts/Default";
import { ErrorBoundary } from "../src/components/shared/ErrorBoundary";
import { SupportChatGlobalStyles } from "../src/components/shared/SupportChat/SupportChatProvider";
import { referrerAtom } from "../src/state/prevPath";
import { Winda } from "../src/utils/Winda";
import { initDatadogRum } from "../src/utils/datadog/initDatadogRum";
import isQualifiedEnabled from "../src/utils/qualified";
import { bronzea, flecha, inter, spaceMono } from "../styles/fonts";

export const defaultRefCodes = {
  aws: "aws_launch_campaign",
  capterra: "capterra",
  hubspot: "hubspot_initial_outreach",
  clerky: "clerky",
  pilot: "pilot",
  orrick: "orrick",
  xero: "xero_lp",
  newzealand: "newzealand",
  yc: "yc_offer",
  payoneer: "payoneer",
};

type UrlParams = typeof marketingAttributionProperties[number] | "nf_ab";
const defaultParamValueMap = {
  ref_code: defaultRefCodes,
};

declare global {
  interface Window {
    qualified: any;
  }
}

const updateUrlParameter = (
  parameter: UrlParams,
  location: Window["location"],
  urlPathValue?: string,
) => {
  const defaultRefCodes = defaultParamValueMap[parameter];

  // Some partners have incorrectly set referral urls
  // we solve to that values for group_code & groupcode are treated the same

  let trueParameter = parameter;
  if (parameter === "groupcode") {
    trueParameter = "group_code";
  }
  const cookieValue: string | undefined = Cookies.get(trueParameter);

  // Always use the most recent refcode.
  let parameterValue = urlPathValue || cookieValue;

  if (!!defaultRefCodes && !parameterValue) {
    parameterValue = defaultRefCodes[location.pathname.replace(/\//g, "")];
  }

  if (!parameterValue) {
    return;
  }
  updateCookies(trueParameter, parameterValue);
};

const initializeUrlParameter = (
  parameter: UrlParams,
  location: Window["location"],
) => {
  const urlPathValue = parseQueryString(parameter, location);
  updateUrlParameter(parameter, location, urlPathValue);
  if (parameter === "rc" && urlPathValue) {
    // Clear the ref_code cookie when setting the rc cookie, or else the ref_code will supercede the rc cookie when read in
    // Form.tsx and SSUSignupLogic.ts
    // Cookies.set("ref_code", undefined, { expires: -1 });
    deleteCookie("ref_code");
  }
};

const onInitialMountTasks = (
  app: CompositionRoot | undefined,
  params: URLSearchParams,
) => {
  if (!app) return;

  initDatadogRum();

  app.analyticsApi.sendTiming(
    createApplicationLaunchedEvent(performance.now()),
  );
  app.analyticsApi.sendTiming(
    createApplicationRenderedEvent(performance.now()),
  );

  // If there is a branch environment variable, append to user traits
  // add the environment they have loaded so we can compare gatsby vs next
  app &&
    app.analyticsApi.identify({
      properties: {
        // tracking param for Comms CHannel NOtification
        cchno: params.get("cchno") || undefined,
      },
    });
};

export type NextPageWithLayout = NextPage & {
  getLayout?: (page: ReactElement) => ReactNode;
};

type AppPropsWithLayout = AppProps & {
  Component: NextPageWithLayout;
};

function App({ Component, pageProps }: AppPropsWithLayout) {
  const prevPath = useRef<string | null>(null);
  const router = useRouter();
  const location = useLocation();
  const setGlobalPreviousPath = useSetAtom(referrerAtom);

  useEffect(() => {
    onInitialMountTasks(app, new URLSearchParams(location.search));

    verifyScriptsLoaded();

    const handleRouteChangeStart = () => {
      const referringPath = window.location.pathname;
      // Get Refferer Path
      prevPath.current = referringPath;
      setGlobalPreviousPath(referringPath);
    };

    const handleRouteChangeComplete = (url: string) => {
      const referrer = prevPath.current || document?.referrer || "";
      const path = standardizePath(url);

      app?.analyticsApi.trackEvent(
        new PageViewedEvent(
          path,
          referrer,
          window.location.search,
          document?.title || "",
          window.location.href,
        ),
      );
    };

    // Fire pageview event for initial render
    handleRouteChangeComplete(window.location.pathname);

    router.events.on("routeChangeComplete", handleRouteChangeComplete);
    router.events.on("routeChangeStart", handleRouteChangeStart);

    // When the component is unmounted, unsubscribe
    // from the event with the `off` method:
    return () => {
      router.events.off("routeChangeComplete", handleRouteChangeComplete);
      router.events.off("routeChangeStart", handleRouteChangeStart);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const propertiesToGet: UrlParams[] = [...marketingAttributionProperties];
    propertiesToGet.map((property) =>
      initializeUrlParameter(property, location),
    );
  }, [location, location.pathname]);

  // Qualified route handling to hide the chat widget on excluded routes
  const wasQualifiedTornDown = useRef(false);
  useEffect(() => {
    if (typeof window !== "undefined") {
      const qualified = window.qualified;
      // Is on an excluded route pattern
      if (qualified && typeof qualified === "function") {
        if (!isQualifiedEnabled(location.pathname)) {
          qualified("teardown");
          wasQualifiedTornDown.current = true;
        } else if (wasQualifiedTornDown.current) {
          qualified("initialize", "R8oV1WdUYLau36SK");
          wasQualifiedTornDown.current = false;
        }
      }
    }
  }, [location.pathname]);

  const getLayout = Component.getLayout || ((page) => <Layout>{page}</Layout>);

  const isDataDogBot =
    typeof window !== "undefined" &&
    window._DATADOG_SYNTHETICS_BROWSER === true;
  return (
    <div
      className={`${inter.className} ${flecha.variable} ${bronzea.variable} ${spaceMono.variable}`}
    >
      <SpeedInsights />
      {/* This is a hack to get the fonts to work in the portal -> see: https://github.com/vercel/next.js/issues/43674#issuecomment-1488309018 */}
      {/* eslint-disable-next-line react/no-unknown-property */}
      <style jsx={true} global={true}>{`
        :root {
          --font-inter: ${inter.style.fontFamily};
          --font-flecha: ${flecha.style.fontFamily};
          --font-bronzea: ${bronzea.style.fontFamily};
          --font-space-mono: ${spaceMono.style.fontFamily};
        }
      `}</style>
      {(Winda.isDataDogBot || isDataDogBot) && (
        <style>{`
          #transcend-consent-manager {
            display: none !important;
          }
        `}</style>
      )}
      <Script
        id={ExternalScripts.CHILIPIPER}
        src="https://js.chilipiper.com/marketing.js"
        type="text/javascript"
        onError={(e) => logScriptLoadingError(ExternalScripts.CHILIPIPER, e)}
      ></Script>
      {!Winda.isDataDogBot && (
        <Script
          id={ExternalScripts.MARKETO}
          strategy="afterInteractive"
          type="text/javascript"
          onError={(e) => logScriptLoadingError(ExternalScripts.MARKETO, e)}
        >{`
          (function() {
            var didInit = false;
            function initMunchkin() {
              if (didInit === false) {
                didInit = true;
                Munchkin.init("166-VEV-188");
              }
            }
            var s = document.createElement("script");
            s.type = "text/javascript";
            s.async = true;
            s.defer = true;
            s.src = "//munchkin.marketo.net/munchkin.js";
            s.onreadystatechange = function() {
              if (
                this.readyState == "complete" ||
                this.readyState == "loaded"
              ) {
                initMunchkin();
              }
            };
            s.onload = initMunchkin;
            document.getElementsByTagName("head")[0].appendChild(s);
          })()
        `}</Script>
      )}
      {/* This SVG doesn't render anything, just adds a global definition for the linear gradient that all other SVGs can reference.  It lives in _app so it's accessible to all SVGS, and is only added once per page */}
      <GradientDefinitionSVG />
      {/* Global styles for HC's chat widget. It needs to be added globally because, once opened, ada iframes won't be unmounted, so we need them to be styled correctly */}
      <SupportChatGlobalStyles />
      <AnalyticsContextProvider
        initialState={initialState}
        segmentClient={app.segmentClient}
        logRocketInstance={app.logRocketClient}
      >
        <ThemeProvider>
          <MetalProvider>
            <SiteProvider
              //TODO: navigators should be removed from site context
              navigators={{ component: NextLink, function: router.push }}
            >
              <LazyMotionProvider>
                <BodyScrollProvider>
                  <DefaultSeo
                    defaultTitle="Brex"
                    titleTemplate="%s | Brex"
                    openGraph={{
                      type: "website",
                      site_name: "Brex",
                      url: getServerSideCurrentUrl(
                        router,
                        "https://www.brex.com",
                      ),
                    }}
                    twitter={{
                      cardType: "summary_large_image",
                      handle: "@brexhq",
                      site: "@brexhq",
                    }}
                    additionalMetaTags={[
                      {
                        property: "google-site-verification",
                        content: "eAZdeidNWAZX4lGjv_8MMSSUyOTNqN6Uw2MGzI-iHU8",
                      },
                    ]}
                    // Because we don't have any "duplicate" pages that are represented by one canonical url, we want to just always set the current url (with no params) as the "canonical" url for this page.
                    canonical={getServerSideCurrentUrl(
                      router,
                      "https://www.brex.com",
                    )}
                  />
                  <OrganizationJsonLd
                    type="Corporation"
                    legalName="Brex"
                    name="Brex"
                    url="https://www.brex.com"
                  />
                  <ErrorBoundary>
                    {getLayout(<Component {...pageProps} />)}
                  </ErrorBoundary>
                </BodyScrollProvider>
              </LazyMotionProvider>
            </SiteProvider>
          </MetalProvider>
        </ThemeProvider>
      </AnalyticsContextProvider>
    </div>
  );
}

export default App;
